import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import "./hs-button.css";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        /**Use only 1 ultra per page so its not overwhelming */
        ultra:
          "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 shadow-lg hover:shadow-xl transition-all duration-200",
        default:
          "bg-card-foreground text-purple-600 hover:bg-gray-100 font-semibold px-8 py-4 shadow-xl dark:bg-gray-200 dark:text-purple-700 dark:hover:bg-gray-300",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "link",
        class: "px-0",
      },
    ],
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  onHoverScroll?: string; // The text to scroll on hover
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onHoverScroll, children, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const Comp = asChild ? Slot : "button";

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(true);
      props.onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(false);
      props.onMouseLeave?.(e);
    };

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          onHoverScroll && "relative overflow-hidden",
          className,
        )}
        ref={ref}
        {...props}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* asChild routes through Radix Slot, which requires a single child —
            the hover-scroll overlay sibling only applies to the plain button. */}
        {asChild ? (
          children
        ) : (
          <>
            {children}
            {onHoverScroll && isHovered && (
              <div className="absolute inset-0 flex items-center bg-gradient-to-r from-purple-600 to-blue-600">
                <div className="flex animate-scroll-left whitespace-nowrap">
                  {Array.from({ length: 20 }, (_, i) => (
                    <span key={i} className="mr-2 flex-shrink-0 font-bold text-xl">
                      {onHoverScroll}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
